import { useState } from 'react';
import { Printer, RotateCcw, FileText, Calendar, Eye, Edit3 } from 'lucide-react';

const HEADER_IMG_SRC = '/assets/reportes/header_beneficencia.png';
const FOOTER_IMG_SRC = '/assets/reportes/footer_beneficencia.png';

const DEFAULT_TITULO = `Consejo Mexicano de Cirugía Pediátrica
Presente.-`;

const DEFAULT_CONTENIDO = `Por medio de la presente hago de su conocimiento que el Dr. José Luis Cadena Santillana, médico especialista en Cirugía Pediátrica, desde el día 02 de agosto del 2011 A la fecha, presta sus servicios en el área de Consulta externa y Hospitalización, realizando los procedimientos quirúrgicos inherentes a su especialidad, dentro de la institución médica SOCIEDAD ESPAÑOLA DE BENEFICENCIA DE PUEBLA, I.A.P., en su modalidad de prestador de servicios independientes sin honorarios, lo que significa que no mantiene una relación laboral médica con nuestra Institución, es decir, no está sujeto a horario alguno, ni dicha Institución es su única fuente de ingresos, por lo que es responsable de sus propias obligaciones y compromisos profesionales.

Se extiende la presente constancia para los fines que el interesado juzgue convenientes.`;

function getTodayString(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatFechaReporte(fechaStr: string): string {
  if (!fechaStr) return '';
  const parts = fechaStr.split('-');
  if (parts.length !== 3) return fechaStr;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  const meses = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];
  const mesNombre = meses[month - 1] || '';
  return `Puebla, Pue., a ${day} de ${mesNombre} del ${year}.`;
}

export default function ReporteConsultaHospitalizacionPage() {
  const [fecha, setFecha] = useState<string>(getTodayString());
  const [titulo, setTitulo] = useState<string>(DEFAULT_TITULO);
  const [contenido, setContenido] = useState<string>(DEFAULT_CONTENIDO);
  const [activeTab, setActiveTab] = useState<'editor' | 'preview'>('editor');

  const handlePrint = () => {
    window.print();
  };

  const handleReset = () => {
    if (window.confirm('¿Desea restablecer los textos a los valores por defecto?')) {
      setFecha(getTodayString());
      setTitulo(DEFAULT_TITULO);
      setContenido(DEFAULT_CONTENIDO);
    }
  };

  const fechaFormateada = formatFechaReporte(fecha);

  return (
    <div className="min-h-screen bg-gray-50 pb-12 print:bg-white print:p-0 print:pb-0">
      {/* Barra de Controles (Oculta al imprimir) */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4 print:hidden">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-primary-600" />
                Constancia de Consulta y Hospitalización
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Generador de constancia institucional - Hospital Beneficencia Española de Puebla
              </p>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={handleReset}
                className="btn btn-secondary flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                title="Restablecer valores por defecto"
              >
                <RotateCcw className="w-4 h-4" />
                Restablecer
              </button>

              <button
                type="button"
                onClick={handlePrint}
                className="btn btn-primary flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 shadow-md transition-all font-medium text-sm"
              >
                <Printer className="w-5 h-5" />
                Imprimir / PDF
              </button>
            </div>
          </div>

          {/* Selector de pestañas para móvil/desktop */}
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={() => setActiveTab('editor')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'editor'
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Edit3 className="w-4 h-4" />
              Editar Formulario
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'preview'
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Eye className="w-4 h-4" />
              Vista Previa
            </button>
          </div>
        </div>

        {/* Panel de Edición */}
        {activeTab === 'editor' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-8 space-y-6">
            {/* Input Fecha */}
            <div>
              <label htmlFor="fecha-input" className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Calendar className="w-4 h-4 text-primary-600" />
                Fecha de la Constancia:
              </label>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <input
                  id="fecha-input"
                  name="fecha"
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm"
                />
                <span className="text-sm font-medium text-gray-600 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
                  Formato en documento: <strong className="text-gray-800">{fechaFormateada}</strong>
                </span>
              </div>
            </div>

            {/* Textarea 1: Título / Destinatario */}
            <div>
              <label htmlFor="titulo-input" className="block text-sm font-semibold text-gray-700 mb-2">
                Título / Destinatario (Encabezado de la carta):
              </label>
              <textarea
                id="titulo-input"
                name="titulo"
                rows={4}
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-sans focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm leading-relaxed"
                placeholder="A QUIEN CORRESPONDA:&#10;Consejo Mexicano..."
              />
              <p className="text-xs text-gray-400 mt-1">
                Aparecerá en el bloque superior izquierdo antes del cuerpo de la constancia.
              </p>
            </div>

            {/* Textarea 2: Contenido Principal */}
            <div>
              <label htmlFor="contenido-input" className="block text-sm font-semibold text-gray-700 mb-2">
                Contenido Principal:
              </label>
              <textarea
                id="contenido-input"
                name="contenido"
                rows={8}
                value={contenido}
                onChange={(e) => setContenido(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-sans focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm leading-relaxed"
                placeholder="Por medio de la presente..."
              />
              <p className="text-xs text-gray-400 mt-1">
                Texto del cuerpo de la constancia. Los saltos de línea y párrafos se respetarán exactamente.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* DOCUMENTO IMPRESO / VISTA PREVIA */}
      <div className={`max-w-4xl mx-auto px-2 sm:px-4 ${activeTab === 'editor' ? 'hidden sm:block print:block' : 'block'}`}>
        <div className="text-xs text-gray-400 text-center mb-2 print:hidden">
          — Vista preliminar del formato impreso (Carta / Letter) —
        </div>

        {/* Hoja de papel formato Carta */}
        <div
          id="documento-constancia"
          className="bg-white mx-auto shadow-2xl print:shadow-none border border-gray-200 print:border-none relative flex flex-col justify-between"
          style={{
            width: '100%',
            maxWidth: '215.9mm',
            minHeight: '279.4mm',
            boxSizing: 'border-box',
            fontFamily: 'Arial, Helvetica, "Nimbus Sans L", sans-serif',
            color: '#111827',
          }}
        >
          {/* 1. ENCABEZADO INSTITUCIONAL */}
          <div className="w-full">
            <img
              src={HEADER_IMG_SRC}
              alt="Hospital Beneficencia Española de Puebla"
              className="w-full h-auto object-contain block select-none"
              style={{ maxHeight: '32mm' }}
            />
          </div>

          {/* 2. CUERPO DEL DOCUMENTO */}
          <div
            className="flex-1 flex flex-col justify-start"
            style={{
              paddingLeft: '24mm',
              paddingRight: '24mm',
              paddingTop: '2mm',
              paddingBottom: '2mm'
            }}
          >
            {/* Fecha (Alineada a la derecha) */}
            <div
              className="text-right text-gray-900 font-normal"
              style={{
                fontSize: '11pt',
                marginTop: '4mm',
                marginBottom: '18mm'
              }}
            >
              {fechaFormateada}
            </div>

            {/* Título / Destinatario */}
            <div
              className="text-left text-gray-900 font-bold leading-relaxed whitespace-pre-line"
              style={{
                fontSize: '11pt',
                marginBottom: '14mm'
              }}
            >
              {titulo}
            </div>

            {/* Contenido Principal (Justificado con Sangría en cada párrafo) */}
            <div className="space-y-4">
              {contenido
                .split(/\n\s*\n/)
                .map((parrafo, idx) => (
                  <p
                    key={idx}
                    className="text-gray-900 text-justify tracking-normal"
                    style={{
                      fontSize: '11pt',
                      lineHeight: '1.9',
                      textIndent: '1.2cm',
                      whiteSpace: 'pre-line'
                    }}
                  >
                    {parrafo.trim()}
                  </p>
                ))}
            </div>
          </div>

          {/* 3. PIE DE PÁGINA INSTITUCIONAL (Firmas y sellos) */}
          <div className="w-full mt-auto">
            <img
              src={FOOTER_IMG_SRC}
              alt="Firmas y Sellos de Certificación"
              className="w-full h-auto object-contain block select-none"
              style={{ maxHeight: '78mm' }}
            />
          </div>
        </div>
      </div>

      {/* ESTILOS DE IMPRESIÓN */}
      <style>{`
        @media print {
          body {
            background-color: #ffffff !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          @page {
            size: letter portrait;
            margin: 0mm;
          }

          #documento-constancia {
            width: 215.9mm !important;
            height: 279.4mm !important;
            min-height: 279.4mm !important;
            max-height: 279.4mm !important;
            margin: 0 auto !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}</style>
    </div>
  );
}
